import { useState, useEffect, useCallback, useMemo } from "react";
import { authFetch } from "../utils/authFetch";
import { Link2, Plus, Ban, Copy, Check, Eye, AlertCircle, Clock, X, Pencil, Search } from "lucide-react";
import TreeAccessPicker from "./TreeAccessPicker";
import { ConfirmDialog } from "./ConfirmDialog";
import type { AccessRule } from "../hooks/useDocAccess";

interface ShareRow {
  id: string;
  token: string;
  rules: AccessRule[];
  name: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  viewCount: number;
  lastViewedAt: string | null;
  createdBy: { email: string | null; name: string | null };
  isActive: boolean;
}

interface Props {
  projectSlug: string;
  isPublished: boolean;
}

// Every link expires, 30 days at most (2026-10-13; the API refuses "never").
const TTL_OPTIONS: Array<{ label: string; hours: number }> = [
  { label: "1 hour", hours: 1 },
  { label: "1 day", hours: 24 },
  { label: "7 days", hours: 24 * 7 },
  { label: "30 days", hours: 24 * 30 },
];

function formatRelative(isoOrNull: string | null, suffix: "expires" | "viewed"): string {
  if (!isoOrNull) return suffix === "expires" ? "Never expires" : "Never viewed";
  const d = new Date(isoOrNull);
  const now = Date.now();
  const diff = d.getTime() - now;
  const past = diff < 0;
  const abs = Math.abs(diff);
  const min = Math.round(abs / 60000);
  const hr = Math.round(min / 60);
  const day = Math.round(hr / 24);
  if (past) {
    if (suffix === "expires") return "Expired";
    if (min < 60) return `${min}m ago`;
    if (hr < 24) return `${hr}h ago`;
    return `${day}d ago`;
  }
  if (min < 60) return `Expires in ${min}m`;
  if (hr < 24) return `Expires in ${hr}h`;
  return `Expires in ${day}d`;
}

function shareUrl(token: string): string {
  return `${window.location.origin}/share/${token}`;
}

function describeRules(rules: AccessRule[]): string {
  if (!rules || rules.length === 0) return "Whole project";
  if (rules.length === 1) {
    const r = rules[0];
    return r.includeChildren ? `${r.slug} (and everything inside)` : r.slug;
  }
  return `${rules.length} sections selected`;
}

type TabKey = "active" | "inactive";

export default function ShareLinksSection({ projectSlug, isPublished }: Props) {
  const [shares, setShares] = useState<ShareRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState<{ kind: "create" } | { kind: "edit"; share: ShareRow } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("active");
  const [search, setSearch] = useState("");
  const [pendingRevoke, setPendingRevoke] = useState<ShareRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const r = await authFetch(`/api/projects/${projectSlug}/shares`);
    if (!r.ok) {
      setError("Failed to load shares");
      setLoading(false);
      return;
    }
    setShares(await r.json());
    setLoading(false);
  }, [projectSlug]);

  useEffect(() => { load(); }, [load]);

  const confirmRevoke = async () => {
    if (!pendingRevoke) return;
    const r = await authFetch(`/api/projects/${projectSlug}/shares/${pendingRevoke.id}`, { method: "DELETE" });
    if (r.ok) load();
    else setError("Failed to revoke");
    setPendingRevoke(null);
  };

  const copyUrl = (s: ShareRow) => {
    navigator.clipboard.writeText(shareUrl(s.token));
    setCopied(s.id);
    setTimeout(() => setCopied(null), 1800);
  };

  if (!isPublished) {
    return (
      <div className="space-y-3">
        <div className="text-sm font-semibold text-slate-700">Share links</div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800 flex items-start gap-2">
          <AlertCircle className="size-4 mt-0.5 shrink-0" />
          <div>
            Publish this project before creating share links. Open the General tab to publish.
          </div>
        </div>
      </div>
    );
  }

  const active = shares.filter(s => s.isActive);
  const inactive = shares.filter(s => !s.isActive);

  // Search matches against the row's display name. Unnamed shares fall
  // back to `describeRules(rules)` (what the row shows in the UI), so
  // both labelled and unlabelled rows are findable. Case-insensitive
  // substring; empty query = show all.
  const matches = (s: ShareRow, q: string) => {
    if (!q) return true;
    const haystack = (s.name || describeRules(s.rules) || "").toLowerCase();
    return haystack.includes(q);
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (tab === "active" ? active : inactive).filter(s => matches(s, q));
  }, [tab, active, inactive, search]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-700">Share links</span>
        <button onClick={() => setDialog({ kind: "create" })}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors">
          <Plus className="size-3.5" /> New link
        </button>
      </div>

      <p className="text-xs text-slate-500">
        Anyone with a share link can view this project without logging in. Useful for sharing with teammates, clients, or partners who don't have a Cortex account.
      </p>

      {error && (
        <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <AlertCircle className="size-3.5" /> {error}
        </div>
      )}

      <div className="flex items-center gap-1 border-b border-slate-200">
        <TabButton active={tab === "active"} count={active.length} onClick={() => setTab("active")}>Active</TabButton>
        <TabButton active={tab === "inactive"} count={inactive.length} onClick={() => setTab("inactive")}>Inactive</TabButton>
      </div>

      {shares.length > 5 && (
        <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 max-w-sm focus-within:ring-1 focus-within:ring-indigo-400 focus-within:border-indigo-400 focus-within:bg-white transition-colors">
          <Search className="size-3.5 text-slate-400 shrink-0" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by name or scope…"
            className="flex-1 text-sm text-slate-700 placeholder:text-slate-400 bg-transparent outline-none"
          />
          {search && (
            <button onClick={() => setSearch("")} className="p-0.5 text-slate-300 hover:text-slate-500" title="Clear">
              <X className="size-3" />
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-400 py-6 text-center">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-xl px-4 py-8 text-center">
          <Link2 className="size-6 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-400">
            {search.trim()
              ? `No ${tab === "active" ? "active" : "inactive"} shares match "${search.trim()}".`
              : tab === "active" ? "No active share links yet." : "No revoked or expired shares."}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
          {visible.map(s => (
            <ShareRowItem
              key={s.id}
              share={s}
              copied={copied === s.id}
              onCopy={() => copyUrl(s)}
              onEdit={() => setDialog({ kind: "edit", share: s })}
              onRevoke={() => setPendingRevoke(s)}
            />
          ))}
        </div>
      )}

      {dialog && (
        <ShareDialog
          projectSlug={projectSlug}
          existing={dialog.kind === "edit" ? dialog.share : null}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); load(); }}
        />
      )}

      {pendingRevoke && (
        <ConfirmDialog
          title="Revoke share link?"
          message={`${pendingRevoke.name ? `"${pendingRevoke.name}" — ` : ""}anyone with this link will lose access immediately. This can't be undone; create a new link if you need to share again.`}
          confirmLabel="Revoke"
          onConfirm={confirmRevoke}
          onCancel={() => setPendingRevoke(null)}
        />
      )}
    </div>
  );
}


function TabButton({ active, count, onClick, children }: {
  active: boolean; count: number; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button onClick={onClick}
      className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
        active
          ? "border-indigo-500 text-indigo-700"
          : "border-transparent text-slate-500 hover:text-slate-700"
      }`}>
      {children}
      <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded ${active ? "bg-indigo-50 text-indigo-600" : "bg-slate-100 text-slate-500"}`}>
        {count}
      </span>
    </button>
  );
}


function ShareRowItem({ share: s, copied, onCopy, onEdit, onRevoke }: {
  share: ShareRow;
  copied: boolean;
  onCopy: () => void;
  onEdit: () => void;
  onRevoke: () => void;
}) {
  const url = shareUrl(s.token);
  return (
    <div className={`px-4 py-3 ${s.isActive ? "" : "opacity-60"}`}>
      <div className="flex items-start gap-3">
        <div className="size-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
          <Link2 className="size-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-slate-700 truncate">
              {s.name || describeRules(s.rules)}
            </span>
            {!s.isActive && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                {s.revokedAt ? "Revoked" : "Expired"}
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5 font-mono break-all">{url}</div>
          <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-400 mt-1.5">
            <span className="flex items-center gap-1"><Clock className="size-3" /> {formatRelative(s.expiresAt, "expires")}</span>
            <span className="flex items-center gap-1"><Eye className="size-3" /> {s.viewCount} view{s.viewCount !== 1 ? "s" : ""}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {s.isActive && (
            <>
              <button onClick={onCopy} title="Copy link"
                className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors">
                {copied ? <Check className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
              </button>
              <button onClick={onEdit} title="Edit"
                className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors">
                <Pencil className="size-3.5" />
              </button>
              <button onClick={onRevoke} title="Revoke"
                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors">
                <Ban className="size-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


/* ── Create / edit dialog ──────────────────────────────────────────── */

function ShareDialog({ projectSlug, existing, onClose, onSaved }: {
  projectSlug: string;
  existing: ShareRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = existing !== null;
  const [name, setName] = useState(existing?.name ?? "");
  const [rules, setRules] = useState<AccessRule[]>(existing?.rules ?? []);
  // For edit: leave ttl unchanged unless the user picks a new option.
  // For create: default to 7 days.
  const [ttlHours, setTtlHours] = useState<number | "unchanged">(
    isEdit ? "unchanged" : 24 * 7,
  );
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setSubmitting(true);
    setErr("");

    if (isEdit) {
      const body: Record<string, unknown> = {
        rules: rules.map(rl => ({ slug: rl.slug, include_children: rl.includeChildren })),
        name: name.trim() || null,
      };
      if (ttlHours !== "unchanged") body.ttl_hours = ttlHours;
      const r = await authFetch(`/api/projects/${projectSlug}/shares/${existing!.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setSubmitting(false);
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErr(d.detail || "Failed to update");
        return;
      }
    } else {
      const r = await authFetch(`/api/projects/${projectSlug}/shares`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rules: rules.map(rl => ({ slug: rl.slug, include_children: rl.includeChildren })),
          ttl_hours: ttlHours === "unchanged" ? 24 * 7 : ttlHours,
          name: name.trim() || null,
        }),
      });
      setSubmitting(false);
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErr(d.detail || "Failed to create");
        return;
      }
    }
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-800">{isEdit ? "Edit share link" : "New share link"}</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100">
            <X className="size-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Label <span className="text-slate-400 font-normal">(optional)</span></label>
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Eng review draft" autoFocus
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1.5">What to share</label>
            <p className="text-[11px] text-slate-400 mb-2">Select sections or pages. Leave everything unchecked to share the whole project.</p>
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 max-h-[40vh] overflow-y-auto">
              <TreeAccessPicker projectSlug={projectSlug} rules={rules} onChange={setRules} />
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">{describeRules(rules)}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1.5">Expires</label>
            <div className="grid grid-cols-4 gap-1.5">
              {TTL_OPTIONS.map(opt => (
                <button key={opt.label} type="button" onClick={() => setTtlHours(opt.hours)}
                  className={`px-2 py-2 rounded-lg text-[11px] font-medium border transition-colors ${ttlHours === opt.hours ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
                  {opt.label}
                </button>
              ))}
            </div>
            {isEdit && (
              <p className="text-[11px] text-slate-400 mt-1.5">
                {ttlHours === "unchanged"
                  ? `Currently: ${formatRelative(existing!.expiresAt, "expires").toLowerCase()}. Pick an option to reset.`
                  : "Expiry will reset from now."} Links last 30 days at most.
              </p>
            )}
          </div>

          {err && (
            <div className="flex items-center gap-2 text-xs text-red-600">
              <AlertCircle className="size-3.5" /> {err}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={submit} disabled={submitting}
            className="px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50">
            {submitting ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save changes" : "Create link")}
          </button>
        </div>
      </div>
    </div>
  );
}
